import React, { useEffect, useState, useRef } from "react";
import { matchesMember, normalize } from "../lib/memberSearch";
import { SignalPulse } from "../components/SignalPulse";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Send, Loader2, Trash2, Lock, Bookmark, Pencil, Share2, X, Repeat2, Search, Flag, Pin, Layers, Ban, Flame, Reply } from "lucide-react";
import api, { CATEGORY_LABELS, CATEGORY_COLORS, CATEGORY_DESC } from "../lib/api";
import { RealmFilter } from "../components/RealmFilter";
import { RealmIcon } from "../lib/realmMeta";
import { CoachTips, useActiveScreenTip } from "../components/CoachTip";
import { VoicesInviteCard } from "./SovereignVoices";
import { InviteNudge } from "../components/InviteNudge";
import { PostMedia, Avatar } from "../components/Media";
import { StreamRefCard } from "../components/StreamShare";
import { WorkshopBoard } from "../components/WorkshopBoard";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";
import { DisturbanceModal } from "../components/DisturbanceModal";
import { RichText } from "../components/RichText";
import { AddToCollectionModal } from "../components/AddToCollectionModal";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

// Hashtags are extracted client-side from the DECRYPTED caption, nothing is ever
// stored/sent in plaintext, so end-to-end encryption is fully preserved.
const HASHTAG_RE = /#([\p{L}\p{N}_]+)/gu;
const extractTags = (text) => {
  if (!text) return [];
  const out = [];
  for (const m of text.matchAll(HASHTAG_RE)) out.push(m[1].toLowerCase());
  return [...new Set(out)];
};

// Render caption text with #hashtags turned into tappable search links and URLs into external links.
const CaptionText = ({ text, onTag }) => <RichText text={text} onTag={onTag} />;

export const CategoryPill = ({ cat }) => (
  <span className="inline-flex items-center gap-1.5 font-body text-[10px] tracking-[0.2em] uppercase text-zinc-400 whitespace-nowrap">
    <span className="cat-dot" style={{ background: CATEGORY_COLORS[cat] }} />
    <RealmIcon realm={cat} size={12} strokeWidth={1.75} style={{ color: CATEGORY_COLORS[cat] }} data-testid="post-realm-glyph" />
    {CATEGORY_LABELS[cat]}
  </span>
);

const ShareModal = ({ post, onClose }) => {
  const { encryptNetText, encryptDm, hasNetwork } = useCrypto();
  const [tab, setTab] = useState("repost"); // repost | dm
  const [comment, setComment] = useState("");
  const [dmText, setDmText] = useState("");
  const [members, setMembers] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    if (tab === "dm" && members === null) {
      api.get("/members").then(({ data }) => setMembers(data)).catch(() => setMembers([]));
    }
    /* eslint-disable-next-line */
  }, [tab]);

  const repost = async () => {
    setBusy(true);
    const payload = { category: post.category, media_path: post.media_path, media_type: post.media_type, reshare_of: post.id };
    if (comment.trim() && hasNetwork()) {
      const enc = await encryptNetText(comment.trim());
      payload.reshare_comment_ct = enc.ct;
      payload.reshare_comment_iv = enc.iv;
    }
    try { await api.post("/posts", payload); setDone("Reposted to your feed."); }
    catch { alert("Could not repost. Please try again."); }
    setBusy(false);
  };

  const sendTo = async (memberId) => {
    setBusy(true);
    let payload = { to_user_id: memberId, post_ref: post.id };
    if (dmText.trim()) {
      try { const enc = await encryptDm(memberId, dmText.trim()); payload = { ...payload, ...enc }; } catch { /* send without note */ }
    }
    try { await api.post("/messages", payload); setDone("Sent privately."); }
    catch { alert("Could not send. Please try again."); }
    setBusy(false);
  };

  const filtered = (members || []).filter((m) => matchesMember(m, search));

  return (
    <div className="fixed inset-0 z-[120] bg-black/85 flex items-center justify-center p-4" data-testid="share-modal" onClick={onClose}>
      <div className="bg-[#121212] border border-white/10 max-w-md w-full p-6 fade-up max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg text-white">Share this signal</h3>
          <button onClick={onClose} data-testid="share-close" className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>

        {done ? (
          <div className="text-center py-8" data-testid="share-done">
            <p className="font-body text-sm text-[#14A89A] mb-6">{done}</p>
            <button onClick={onClose} className="bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#2BC4B5] transition-colors font-body">Done</button>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-5">
              <button onClick={() => setTab("repost")} data-testid="share-tab-repost" className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-body text-xs tracking-wider uppercase border transition-colors ${tab === "repost" ? "border-[#A855F7] text-[#A855F7]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                <Repeat2 size={15} /> Repost
              </button>
              <button onClick={() => setTab("dm")} data-testid="share-tab-dm" className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-body text-xs tracking-wider uppercase border transition-colors ${tab === "dm" ? "border-[#A855F7] text-[#A855F7]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                <Send size={15} /> Send to member
              </button>
            </div>

            {tab === "repost" ? (
              <div className="space-y-4">
                <p className="font-body text-sm text-zinc-400">Repost this to your own feed, with attribution to the original sovereign.</p>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add your words (optional)" data-testid="repost-comment-input" rows={3} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none" />
                <button onClick={repost} disabled={busy} data-testid="repost-submit" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3.5 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : <><Repeat2 size={16} /> Repost to my feed</>}
                </button>
              </div>
            ) : (
              <div className="flex flex-col min-h-0 flex-1">
                <textarea value={dmText} onChange={(e) => setDmText(e.target.value)} placeholder="Add a private message (optional)" data-testid="forward-message-input" rows={2} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none mb-3" />
                <div className="flex items-center gap-2 border-b border-white/15 mb-2">
                  <Search size={15} className="text-zinc-500" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a member" data-testid="forward-search-input" className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none font-body" />
                </div>
                <div className="overflow-y-auto flex-1 -mx-1">
                  {members === null ? (
                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-500" size={18} /></div>
                  ) : filtered.length === 0 ? (
                    <p className="font-body text-xs text-zinc-600 px-1 py-4">No members found.</p>
                  ) : filtered.map((m) => (
                    <button key={m.id} onClick={() => sendTo(m.id)} disabled={busy} data-testid={`forward-to-${m.id}`} className="w-full flex items-center gap-3 px-1 py-2.5 hover:bg-white/5 transition-colors text-left disabled:opacity-40">
                      <Avatar name={m.name} src={m.avatar} size={36} />
                      <div className="min-w-0">
                        <div className="font-heading text-sm text-white truncate">{m.name}</div>
                        <div className="font-body text-xs text-zinc-500 truncate">{m.headline}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const PostCard = ({ post, onNav, onDelete, onUnsave, allowNote, currentUser, defaultOpenComments, onTag, onChanged, profileView }) => {
  const { decryptNetText, encryptNetText, hasNetwork, netReady, status } = useCrypto();
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes_count);
  const [saved, setSaved] = useState(post.saved);
  const [note, setNote] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [editingNote, setEditingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [cTexts, setCTexts] = useState({});
  const [cCount, setCCount] = useState(post.comments_count);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const cInputRef = useRef(null);

  const startReply = (c) => {
    setReplyTo({ id: c.author.id, name: c.author.name });
    const mention = `@${c.author.name} `;
    setText((t) => (t.startsWith("@") ? t : mention + t));
    setShowComments(true);
    setTimeout(() => { if (cInputRef.current) { cInputRef.current.focus(); autoGrowComment(cInputRef.current); } }, 0);
  };
  const cancelReply = () => { setReplyTo(null); };
  const autoGrowComment = (el) => { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 140) + "px"; };
  const [loadingC, setLoadingC] = useState(false);
  const [caption, setCaption] = useState(post.enc ? null : post.caption);
  const [showShare, setShowShare] = useState(false);
  const [flagTarget, setFlagTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reshareComment, setReshareComment] = useState(null);
  const [editingCap, setEditingCap] = useState(false);
  const [capDraft, setCapDraft] = useState("");
  const [savingCap, setSavingCap] = useState(false);
  const [capEdited, setCapEdited] = useState(post.edited_at);
  const [editingC, setEditingC] = useState(null);
  const [cDraft, setCDraft] = useState("");
  const canDelete = currentUser && (currentUser.id === post.author.id || currentUser.role === "admin");
  const isOwnPost = currentUser && currentUser.id === post.author.id;
  const isFounder = currentUser && currentUser.is_founder;
  const [pinned, setPinned] = useState(post.pinned);
  const [profilePinned, setProfilePinned] = useState(post.profile_pinned);
  const [showCollect, setShowCollect] = useState(false);
  const isTextPost = !post.stream_ref && (!post.media || post.media.length === 0) && !post.media_path;
  const realmColor = CATEGORY_COLORS[(post.categories && post.categories[0]) || post.category] || "#14A89A";
  const [blockedHidden, setBlockedHidden] = useState(false);
  const blockAuthor = async () => {
    if (!window.confirm(`Block ${post.author?.name || "this member"}? You won't see each other's posts and neither of you can message the other. It's private, they're never told, and the Council isn't involved.`)) return;
    try { await api.post("/blocks", { member_id: post.author.id }); setBlockedHidden(true); if (onChanged) onChanged(); }
    catch { alert("Could not block this member. Please try again."); }
  };
  const pinGlow = useActiveScreenTip("profile-own") === "profile-own";

  const togglePin = async () => {
    const prev = pinned;
    setPinned(!prev);
    try {
      const { data } = await api.patch(`/posts/${post.id}/pin`);
      setPinned(data.pinned);
      if (onChanged) onChanged();
    } catch {
      setPinned(prev);
      alert("Could not update the pin. Please try again.");
    }
  };

  const toggleProfilePin = async () => {
    const prev = profilePinned;
    setProfilePinned(!prev);
    try {
      const { data } = await api.patch(`/posts/${post.id}/profile-pin`);
      setProfilePinned(data.profile_pinned);
      if (onChanged) onChanged();
    } catch (e) {
      setProfilePinned(prev);
      alert(e?.response?.data?.detail || "Could not update. Please try again.");
    }
  };

  useEffect(() => {
    (async () => {
      if (!post.reshare_comment_ct) return;
      if (!netReady && status !== "unlocked") return; // wait for the key (first-login race)
      const pt = await decryptNetText(post.reshare_comment_ct, post.reshare_comment_iv);
      setReshareComment(pt === null ? "__LOCKED__" : pt);
    })();
    /* eslint-disable-next-line */
  }, [post.id, netReady, status]);

  useEffect(() => {
    (async () => {
      if (!(post.enc && post.caption_ct)) return;
      if (!netReady && status !== "unlocked") return; // wait for the key (first-login race)
      const pt = await decryptNetText(post.caption_ct, post.caption_iv);
      setCaption(pt === null ? "__LOCKED__" : pt);
    })();
    /* eslint-disable-next-line */
  }, [post.id, netReady, status]);

  useEffect(() => {
    (async () => {
      if (!allowNote) return;
      if (post.note_enc && post.note_ct) {
        if (!netReady && status !== "unlocked") return; // wait for the key (first-login race)
        const pt = await decryptNetText(post.note_ct, post.note_iv);
        setNote(pt === null ? "__LOCKED__" : pt);
      } else {
        setNote(post.note || "");
      }
    })();
    /* eslint-disable-next-line */
  }, [post.id, netReady, status]);

  const saveNote = async () => {
    setSavingNote(true);
    let payload = { note: noteDraft };
    if (noteDraft && hasNetwork()) {
      const enc = await encryptNetText(noteDraft);
      payload = { note_ct: enc.ct, note_iv: enc.iv, enc: true, note: "" };
    }
    try {
      await api.put(`/posts/${post.id}/note`, payload);
      setNote(noteDraft);
      setEditingNote(false);
    } catch {
      alert("Could not save your note. Please try again.");
    }
    setSavingNote(false);
  };

  const toggleLike = async () => {
    const { data } = await api.post(`/posts/${post.id}/like`);
    setLiked(data.liked); setLikes(data.likes_count);
  };

  const saveCaption = async () => {
    setSavingCap(true);
    let payload = { caption: capDraft };
    if (capDraft && post.enc && hasNetwork()) {
      const enc = await encryptNetText(capDraft);
      payload = { caption_ct: enc.ct, caption_iv: enc.iv, enc: true, caption: "" };
    }
    try {
      const { data } = await api.patch(`/posts/${post.id}/caption`, payload);
      setCaption(capDraft);
      setCapEdited(data.edited_at);
      setEditingCap(false);
    } catch {
      alert("Could not update the caption. Please try again.");
    }
    setSavingCap(false);
  };

  const saveCommentEdit = async (cid) => {
    if (!cDraft.trim()) return;
    let payload = { text: cDraft };
    if (hasNetwork()) { const enc = await encryptNetText(cDraft); payload = { ct: enc.ct, iv: enc.iv, enc: true, text: "" }; }
    try {
      const { data } = await api.patch(`/comments/${cid}`, payload);
      setComments((cs) => cs.map((c) => c.id === cid ? { ...c, ...data } : c));
      setCTexts((m) => ({ ...m, [cid]: cDraft }));
      setEditingC(null);
    } catch {
      alert("Could not update the comment. Please try again.");
    }
  };

  const toggleSave = async () => {
    const next = !saved;
    setSaved(next);
    try {
      await api.post(`/posts/${post.id}/save`);
      if (!next && onUnsave) onUnsave(post.id);
    } catch {
      setSaved(!next);
    }
  };

  const decryptComment = async (c) => {
    if (!c.enc) return c.text;
    const pt = await decryptNetText(c.ct, c.iv);
    return pt === null ? "__LOCKED__" : pt;
  };

  const loadComments = async () => {
    if (!showComments) {
      setLoadingC(true);
      const { data } = await api.get(`/posts/${post.id}/comments`);
      setComments(data);
      const map = {};
      for (const c of data) map[c.id] = await decryptComment(c);
      setCTexts(map);
      setLoadingC(false);
    }
    setShowComments(!showComments);
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    let payload = { text };
    if (hasNetwork()) { const enc = await encryptNetText(text); payload = { ct: enc.ct, iv: enc.iv, enc: true, text: "" }; }
    if (replyTo) payload.reply_to = replyTo.id;
    const { data } = await api.post(`/posts/${post.id}/comments`, payload);
    setComments([...comments, data]);
    setCTexts((m) => ({ ...m, [data.id]: text }));
    setText(""); setCCount(cCount + 1); setReplyTo(null);
    if (cInputRef.current) cInputRef.current.style.height = "auto";
  };

  const likeComment = async (cid) => {
    setComments((cs) => cs.map((c) => c.id === cid ? { ...c, liked: !c.liked, likes_count: (c.likes_count || 0) + (c.liked ? -1 : 1) } : c));
    try {
      const { data } = await api.post(`/comments/${cid}/like`);
      setComments((cs) => cs.map((c) => c.id === cid ? { ...c, liked: data.liked, likes_count: data.likes_count } : c));
    } catch {
      setComments((cs) => cs.map((c) => c.id === cid ? { ...c, liked: !c.liked, likes_count: (c.likes_count || 0) + (c.liked ? -1 : 1) } : c));
    }
  };

  // Auto-open comments when arriving from a notification deep link.
  useEffect(() => {
    if (defaultOpenComments) loadComments();
    /* eslint-disable-next-line */
  }, []);

  if (blockedHidden) return null;

  // Media posts: actions under the media, then caption. Text posts: actions under the text.
  const actionBar = (
  <div className="flex items-center gap-5" data-testid="post-action-bar">
      <button onClick={toggleLike} data-testid="post-like-button" className="flex items-center gap-2 group">
        <Heart size={20} strokeWidth={1.5} className={liked ? "fill-[#14A89A] text-[#14A89A]" : "text-zinc-300 group-hover:text-white"} />
        <span className="font-body text-sm text-zinc-300">{likes}</span>
      </button>
      <button onClick={loadComments} data-testid="post-comment-toggle" className="flex items-center gap-2 group">
        <MessageCircle size={20} strokeWidth={1.5} className="text-zinc-300 group-hover:text-white" />
        <span className="font-body text-sm text-zinc-300">{cCount}</span>
      </button>
      <button onClick={() => setShowShare(true)} data-testid="post-share-button" title="Share" className="flex items-center gap-2 group">
        <Share2 size={20} strokeWidth={1.5} className="text-zinc-300 group-hover:text-white" />
        {post.reshares_count > 0 && <span className="font-body text-sm text-zinc-300" data-testid="reshares-count">{post.reshares_count}</span>}
      </button>
      {!isOwnPost && (
        <button onClick={() => setFlagTarget({ type: "post", id: post.id, authorId: post.author.id, snippet: (caption && caption !== "__LOCKED__") ? caption : "(media post, no readable caption)" })} data-testid="post-flag-button" title="Report a disturbance" className="group">
          <Flag size={18} strokeWidth={1.5} className="text-zinc-500 group-hover:text-[#A855F7]" />
        </button>
      )}
      {!isOwnPost && (
        <button onClick={blockAuthor} data-testid={`post-block-${post.id}`} title="Block this member" className="group">
          <Ban size={18} strokeWidth={1.5} className="text-zinc-500 group-hover:text-red-500" />
        </button>
      )}
      <button onClick={toggleSave} data-testid="post-save-button" title={saved ? "Saved" : "Save"} className="ml-auto group">
        <Bookmark size={20} strokeWidth={1.5} className={saved ? "fill-[#A855F7] text-[#A855F7]" : "text-zinc-300 group-hover:text-white"} />
      </button>
    </div>
  );

  return (
    <article className={`flex flex-col gap-4 fade-up ${pinned ? "border-l-2 border-[#14A89A] pl-4 -ml-4" : (profileView && profilePinned ? "border-l-2 border-[#D4AF37] pl-4 -ml-4" : "")}`} data-testid="post-card">
      {pinned && (
        <div className="flex items-center gap-1.5 text-[#14A89A]" data-testid="pinned-badge">
          <Pin size={13} className="fill-[#14A89A]" />
          <span className="font-body text-[10px] tracking-[0.2em] uppercase">Pinned by Council</span>
        </div>
      )}
      {profileView && profilePinned && !pinned && (
        <div className="flex items-center gap-1.5 text-[#D4AF37]" data-testid="profile-pinned-badge">
          <Pin size={13} className="fill-[#D4AF37]" />
          <span className="font-body text-[10px] tracking-[0.2em] uppercase">Featured</span>
        </div>
      )}
      <div className="flex items-start gap-3">
        <button onClick={() => onNav(`/profile/${post.author.id}`)} className="shrink-0 group">
          <Avatar name={post.author.name} src={post.author.avatar} size={44} />
        </button>
        <div className="min-w-0 flex-1">
          <button onClick={() => onNav(`/profile/${post.author.id}`)} className="block text-left min-w-0 max-w-full group">
            <div className="font-heading text-sm text-white group-hover:text-[#14A89A] transition-colors truncate">{post.author.name}</div>
            <div className="font-body text-xs text-zinc-500 truncate">{post.author.headline}</div>
          </button>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="post-realm-pills">
            {((post.categories && post.categories.length ? post.categories : [post.category]).filter(Boolean)).map((c) => <CategoryPill key={c} cat={c} />)}
          </div>
        </div>
        <div className="shrink-0 flex items-start gap-3">
          <div className="font-body text-[10px] text-zinc-600 whitespace-nowrap">{timeAgo(post.created_at)}</div>
          {isFounder && (
            <button onClick={togglePin} data-testid={`pin-post-${post.id}`} title={pinned ? "Unpin from top" : "Pin to top of feed"} className={`transition-colors -mt-0.5 ${pinned ? "text-[#14A89A]" : "text-zinc-600 hover:text-[#14A89A]"}`}>
              <Pin size={16} className={pinned ? "fill-[#14A89A]" : ""} />
            </button>
          )}
          {isOwnPost && profileView && (
            <button onClick={toggleProfilePin} data-testid={`profile-pin-post-${post.id}`} title={profilePinned ? "Unpin from your profile" : "Pin to your profile (up to 5)"} className={`transition-colors -mt-0.5 ${profilePinned ? "text-[#D4AF37]" : "text-zinc-600 hover:text-[#D4AF37]"} ${pinGlow && !profilePinned ? "pin-pulse" : ""}`}>
              <Pin size={16} className={profilePinned ? "fill-[#D4AF37]" : ""} />
            </button>
          )}
          {isOwnPost && profileView && (
            <button onClick={() => setShowCollect(true)} data-testid={`add-to-collection-${post.id}`} title={post.collection_id ? "In a collection: manage" : "Add to a collection"} className={`transition-colors -mt-0.5 ${post.collection_id ? "text-[#14A89A]" : "text-zinc-600 hover:text-[#14A89A]"}`}>
              <Layers size={16} />
            </button>
          )}
          {canDelete && (
            <button onClick={() => setConfirmDelete(true)} data-testid={`delete-post-${post.id}`} className="text-zinc-600 hover:text-red-500 transition-colors -mt-0.5">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {showCollect && <AddToCollectionModal post={post} myId={currentUser?.id} onClose={() => setShowCollect(false)} onChanged={() => onChanged && onChanged()} />}
      {post.reposted_from && (
        <div className="flex items-center gap-1.5 -mt-1" data-testid="reposted-badge">
          <Repeat2 size={14} className="text-[#14A89A]" />
          <span className="font-body text-xs text-zinc-500">Reposted from <span className="text-zinc-300">{post.reposted_from.author_name}</span></span>
        </div>
      )}
      {reshareComment && reshareComment !== "__LOCKED__" && (
        <p className="font-body text-sm text-zinc-300 leading-relaxed break-words whitespace-pre-wrap" data-testid="reshare-comment">{reshareComment}</p>
      )}

      {(post.stream_ref || !isTextPost) && (
        <div data-testid="post-media-frame">
          {post.stream_ref
            ? <StreamRefCard streamRef={post.stream_ref} />
            : <PostMedia media={post.media} enc={post.enc} contain />}
        </div>
      )}

      {!isTextPost && actionBar}

      {editingCap ? (
        <div className="space-y-2" data-testid="caption-editor">
          <textarea value={capDraft} onChange={(e) => setCapDraft(e.target.value)} rows={3} data-testid="caption-textarea" placeholder="Write a caption..." className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none" />
          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => setEditingCap(false)} data-testid="caption-cancel-button" className="font-body text-xs text-zinc-400 hover:text-white px-3 py-1.5">Cancel</button>
            <button onClick={saveCaption} disabled={savingCap} data-testid="caption-save-button" className="font-body text-xs bg-[#14A89A] text-[#0A0A0A] font-medium px-4 py-1.5 hover:bg-[#2BC4B5] transition-colors flex items-center gap-1.5 disabled:opacity-40">{savingCap && <Loader2 size={12} className="animate-spin" />} Save</button>
          </div>
        </div>
      ) : caption === "__LOCKED__" ? (
        <p className="font-body text-xs text-zinc-600 flex items-center gap-1"><Lock size={12} /> Encrypted {isTextPost ? "note" : "caption"} - unlock to view</p>
      ) : (caption !== null && caption !== "") ? (
        isTextPost ? (
          <div data-testid="post-text-note" className="rounded-sm border-l-2 px-5 py-4" style={{ borderColor: realmColor, background: `${realmColor}14` }}>
            <p className="font-heading text-lg md:text-xl text-zinc-100 leading-relaxed break-words whitespace-pre-wrap">
              <CaptionText text={caption} onTag={onTag} />
              {capEdited && <span className="text-zinc-600 text-xs ml-1.5 font-body" data-testid="caption-edited-tag">(edited)</span>}
              {isOwnPost && <button onClick={() => { setCapDraft(caption); setEditingCap(true); }} data-testid="caption-edit-button" title="Edit note" className="text-zinc-600 hover:text-[#14A89A] ml-2 align-middle inline-flex"><Pencil size={14} /></button>}
            </p>
          </div>
        ) : (
        <p className="font-body text-sm text-zinc-300 leading-relaxed break-words whitespace-pre-wrap">
          <span className="text-white font-medium mr-2">{post.author.name}</span><CaptionText text={caption} onTag={onTag} />
          {capEdited && <span className="text-zinc-600 text-xs ml-1.5" data-testid="caption-edited-tag">(edited)</span>}
          {isOwnPost && <button onClick={() => { setCapDraft(caption); setEditingCap(true); }} data-testid="caption-edit-button" title="Edit caption" className="text-zinc-600 hover:text-[#14A89A] ml-2 align-middle inline-flex"><Pencil size={12} /></button>}
        </p>
        )
      ) : (isOwnPost && caption === "") ? (
        <button onClick={() => { setCapDraft(""); setEditingCap(true); }} data-testid="caption-add-button" className="font-body text-xs text-[#14A89A] hover:text-[#2BC4B5] flex items-center gap-1.5 transition-colors self-start"><Pencil size={13} /> Add a caption</button>
      ) : null}

      {isTextPost && actionBar}

      {allowNote && (
        <div className="border border-[#A855F7]/30 bg-[#A855F7]/[0.06] p-3" data-testid="saved-note">
          {editingNote ? (
            <div className="space-y-2">
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Why are you keeping this? (private to you)"
                data-testid="note-input"
                rows={3}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#A855F7] font-body resize-none"
              />
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setEditingNote(false)} data-testid="note-cancel-button" className="font-body text-xs text-zinc-400 hover:text-white px-3 py-1.5">Cancel</button>
                <button onClick={saveNote} disabled={savingNote} data-testid="note-save-button" className="font-body text-xs bg-[#A855F7] text-white px-4 py-1.5 hover:bg-[#9333EA] transition-colors flex items-center gap-1.5">
                  {savingNote && <Loader2 size={12} className="animate-spin" />} Save note
                </button>
              </div>
            </div>
          ) : note === "__LOCKED__" ? (
            <p className="font-body text-xs text-zinc-500 flex items-center gap-1"><Lock size={12} /> Encrypted note - unlock to view</p>
          ) : note ? (
            <div className="flex items-start gap-2">
              <p className="font-body text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap flex-1" data-testid="note-text">{note}</p>
              <button onClick={() => { setNoteDraft(note); setEditingNote(true); }} data-testid="note-edit-button" className="text-[#A855F7]/80 hover:text-[#A855F7] shrink-0 mt-0.5"><Pencil size={14} /></button>
            </div>
          ) : (
            <button onClick={() => { setNoteDraft(""); setEditingNote(true); }} data-testid="note-add-button" className="font-body text-xs text-[#A855F7] hover:text-[#C084FC] flex items-center gap-1.5 transition-colors">
              <Pencil size={13} /> Add a private note
            </button>
          )}
        </div>
      )}

      {showComments && (
        <div className="border-t border-white/10 pt-4 space-y-4">
          {loadingC ? <Loader2 className="animate-spin text-zinc-500" size={18} /> : (
            comments.length === 0 ? <p className="font-body text-xs text-zinc-600">No exchanges yet.</p> :
            comments.map((c) => (
              <div key={c.id} className="flex gap-3 group/comment" data-testid="comment-item">
                <Avatar name={c.author.name} src={c.author.avatar} size={28} />
                <div className="flex-1 min-w-0">
                  <span className="font-body text-xs text-white font-medium mr-2">{c.author.name}</span>
                  {editingC === c.id ? (
                    <div className="mt-1.5 space-y-2">
                      <input value={cDraft} onChange={(e) => setCDraft(e.target.value)} data-testid={`comment-edit-input-${c.id}`} className="w-full bg-transparent border-b border-white/15 px-0 py-1.5 text-xs text-white focus:outline-none focus:border-[#14A89A] font-body" />
                      <div className="flex items-center gap-3">
                        <button onClick={() => saveCommentEdit(c.id)} data-testid={`comment-edit-save-${c.id}`} className="font-body text-[11px] bg-[#14A89A] text-[#0A0A0A] font-medium px-3 py-1 hover:bg-[#2BC4B5] transition-colors">Save</button>
                        <button onClick={() => setEditingC(null)} data-testid={`comment-edit-cancel-${c.id}`} className="font-body text-[11px] text-zinc-400 hover:text-white">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <span className="font-body text-xs text-zinc-400">
                      {cTexts[c.id] === "__LOCKED__" ? <span className="italic flex items-center gap-1 inline-flex"><Lock size={10} /> Encrypted</span> : <RichText text={cTexts[c.id]} />}
                      {c.edited_at && cTexts[c.id] !== "__LOCKED__" && <span className="text-zinc-600 ml-1.5" data-testid={`comment-edited-${c.id}`}>(edited)</span>}
                    </span>
                  )}
                  {editingC !== c.id && <div className="font-body text-[10px] text-zinc-600 mt-1" data-testid={`comment-time-${c.id}`}>{fmtDateTime(c.created_at)}</div>}
                </div>
                {editingC !== c.id && (
                  <button onClick={() => likeComment(c.id)} data-testid={`comment-like-${c.id}`} title="Appreciate" className="shrink-0 flex items-center gap-1 text-zinc-500 hover:text-[#14A89A] transition-colors">
                    <Heart size={13} className={c.liked ? "fill-[#14A89A] text-[#14A89A]" : ""} />
                    {c.likes_count > 0 && <span className="font-body text-[11px]">{c.likes_count}</span>}
                  </button>
                )}
                {editingC !== c.id && currentUser && c.author.id === currentUser.id && cTexts[c.id] !== "__LOCKED__" && (
                  <button onClick={() => { setCDraft(cTexts[c.id] || ""); setEditingC(c.id); }} data-testid={`comment-edit-${c.id}`} title="Edit comment" className="shrink-0 text-zinc-600 hover:text-[#14A89A] opacity-0 group-hover/comment:opacity-100 transition-opacity">
                    <Pencil size={13} />
                  </button>
                )}
                {editingC !== c.id && currentUser && c.author.id !== currentUser.id && (
                  <button onClick={() => startReply(c)} data-testid={`comment-reply-${c.id}`} title={`Reply to ${c.author.name}`} className="shrink-0 text-zinc-600 hover:text-[#14A89A] opacity-0 group-hover/comment:opacity-100 transition-opacity">
                    <Reply size={13} />
                  </button>
                )}
                {editingC !== c.id && currentUser && c.author.id !== currentUser.id && (
                  <button onClick={() => setFlagTarget({ type: "comment", id: c.id, authorId: c.author.id, snippet: cTexts[c.id] && cTexts[c.id] !== "__LOCKED__" ? cTexts[c.id] : "(encrypted comment)" })} data-testid={`comment-flag-${c.id}`} title="Report a disturbance" className="shrink-0 text-zinc-600 hover:text-[#A855F7] opacity-0 group-hover/comment:opacity-100 transition-opacity">
                    <Flag size={13} />
                  </button>
                )}
              </div>
            ))
          )}
          {replyTo && (
            <div className="flex items-center gap-2 -mb-1" data-testid="reply-indicator">
              <span className="font-body text-[11px] text-[#14A89A]">Replying to {replyTo.name}</span>
              <button type="button" onClick={cancelReply} data-testid="reply-cancel" className="text-zinc-500 hover:text-white"><X size={12} /></button>
            </div>
          )}
          <form onSubmit={addComment} className="flex items-end gap-2 pt-2">
            <textarea
              ref={cInputRef}
              value={text}
              onChange={(e) => { setText(e.target.value); autoGrowComment(e.target); }}
              placeholder="Leave a signal..."
              rows={1}
              data-testid="comment-input"
              className="flex-1 resize-none max-h-[140px] overflow-y-auto bg-transparent border-b border-white/15 px-0 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body leading-snug"
            />
            <button type="submit" data-testid="comment-submit-button" className="text-[#14A89A] hover:text-[#2BC4B5] shrink-0 pb-2"><Send size={18} /></button>
          </form>
        </div>
      )}
      {showShare && <ShareModal post={post} onClose={() => setShowShare(false)} />}
      {flagTarget && <DisturbanceModal target={flagTarget} onClose={() => setFlagTarget(null)} />}
      {confirmDelete && (
        <div className="fixed inset-0 z-[130] bg-black/85 flex items-center justify-center p-4" data-testid="delete-confirm-modal" onClick={() => setConfirmDelete(false)}>
          <div className="bg-[#121212] border border-white/10 max-w-sm w-full p-6 fade-up" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg text-white mb-2">Delete this transmission?</h3>
            <p className="font-body text-sm text-zinc-400 mb-6 leading-snug">This permanently removes the post for everyone. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(false)} data-testid="delete-cancel" className="flex-1 border border-white/15 text-white font-medium px-4 py-3 hover:bg-white/5 transition-colors font-body text-sm">Cancel</button>
              <button onClick={() => { setConfirmDelete(false); onDelete(post.id); }} data-testid="delete-confirm" className="flex-1 bg-red-600 text-white font-medium px-4 py-3 hover:bg-red-500 transition-colors font-body text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default function Feed({ refreshKey }) {
  const [posts, setPosts] = useState(null);
  const [sel, setSel] = useState({ params: {}, realm: "", group: "" });
  const [query, setQuery] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allPosts, setAllPosts] = useState(null); // full network set (search mode)
  const [allIndex, setAllIndex] = useState({});   // id -> searchable text
  const [allTags, setAllTags] = useState({});      // id -> [tags]
  const [searchLoading, setSearchLoading] = useState(false);
  const capCacheRef = useRef({}); // id -> {text, tags} decrypted once
  const navigate = useNavigate();
  const { user } = useAuth();
  const { decryptNetText } = useCrypto();
  const PAGE = 12;

  const load = async () => {
    setPosts(null);
    const { data } = await api.get("/posts", { params: { ...sel.params, skip: 0, limit: PAGE } });
    setPosts(data);
    setHasMore(data.length === PAGE);
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/posts", { params: { ...sel.params, skip: posts.length, limit: PAGE } });
      setPosts((p) => [...p, ...data]);
      setHasMore(data.length === PAGE);
    } finally { setLoadingMore(false); }
  };

  // Fetch the whole network (paged) for search, global across all realms.
  const loadAll = async () => {
    setSearchLoading(true);
    try {
      const CHUNK = 50, CAP = 800;
      let skip = 0, acc = [];
      while (skip < CAP) {
        const { data } = await api.get("/posts", { params: { skip, limit: CHUNK } });
        acc = acc.concat(data);
        if (data.length < CHUNK) break;
        skip += CHUNK;
      }
      setAllPosts(acc);
    } finally { setSearchLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sel, refreshKey]);
  // Invalidate the cached full set when the feed is refreshed.
  useEffect(() => { setAllPosts(null); }, [refreshKey]);

  const q = normalize(query);
  const isTag = q.startsWith("#");
  const tagQuery = isTag ? q.slice(1) : "";

  // Load the full network the first time a search begins.
  useEffect(() => {
    if (q && allPosts === null && !searchLoading) loadAll();
    /* eslint-disable-next-line */
  }, [q]);

  // Build an on-device search index over the full set. Nothing leaves the device.
  useEffect(() => {
    if (!allPosts) return;
    let cancelled = false;
    (async () => {
      const idx = {}, tags = {}, cache = capCacheRef.current;
      for (const p of allPosts) {
        if (!cache[p.id]) {
          let cap = p.enc ? "" : (p.caption || "");
          if (p.enc && p.caption_ct) {
            const pt = await decryptNetText(p.caption_ct, p.caption_iv);
            cap = pt && pt !== "__LOCKED__" ? pt : "";
          }
          cache[p.id] = { text: cap, tags: extractTags(cap) };
        }
        const c = cache[p.id];
        idx[p.id] = normalize([c.text, p.author?.name, p.author?.headline, ((p.categories && p.categories.length ? p.categories : [p.category]).filter(Boolean).map((k) => CATEGORY_LABELS[k]).join(" ")), p.reposted_from?.author_name]
          .filter(Boolean).join(" "));
        tags[p.id] = c.tags;
      }
      if (!cancelled) { setAllIndex(idx); setAllTags(tags); }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [allPosts]);

  const handleDelete = async (postId) => {
    const prev = posts;
    setPosts((p) => p.filter((x) => x.id !== postId));
    setAllPosts((p) => (p ? p.filter((x) => x.id !== postId) : p));
    try {
      await api.delete(`/posts/${postId}`);
    } catch {
      setPosts(prev);
      alert("Could not delete the post. Please try again.");
    }
  };

  const searching = q.length > 0;
  const visible = !searching
    ? posts
    : (allPosts || []).filter((p) => isTag
        ? (allTags[p.id] || []).includes(tagQuery)
        : (allIndex[p.id] || "").includes(q));

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
      <header className="mb-8">
        <h1 className="font-heading text-3xl font-medium tracking-tight text-[#14A89A]">The Signal</h1>
        <p className="font-body text-sm text-zinc-500 mt-1">Sovereigns creating &amp; sharing, privately &amp; decentralized.</p>
      </header>

      <div className="flex items-center gap-2 border border-white/15 px-3 mb-6 focus-within:border-[#14A89A] transition-colors">
        <Search size={16} className="text-zinc-500 shrink-0" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search #hashtags, captions, members, realms..." data-testid="signal-search-input" className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none font-body" />
        {query && <button onClick={() => setQuery("")} data-testid="signal-search-clear" className="text-zinc-500 hover:text-white shrink-0"><X size={16} /></button>}
      </div>

      {searching ? (
        <>
          {isTag && (
            <p className="font-body text-sm text-zinc-400 mb-6" data-testid="tag-results-header">
              Signals tagged <span className="text-[#14A89A]">#{tagQuery}</span>
              {!searchLoading && <span className="text-zinc-600"> · {visible.length}</span>}
            </p>
          )}
          {searchLoading && allPosts === null ? (
            <div className="flex justify-center py-20"><SignalPulse size={44} /></div>
          ) : visible.length === 0 ? (
            <div className="text-center py-20 border border-white/10" data-testid="search-empty">
              <p className="font-heading text-xl text-white mb-2">No signals match "{query}"</p>
              <p className="font-body text-sm text-zinc-500">{isTag ? "No one has used this hashtag yet, be the first." : "Try another keyword, member or realm."}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-12">
              {visible.map((p) => <PostCard key={p.id} post={p} onNav={navigate} onDelete={handleDelete} currentUser={user} onTag={setQuery} />)}
            </div>
          )}
        </>
      ) : (
        <>
          <CoachTips screen="feed" />
          {user?.beacon_nudge && (
            <a href="/beacon" data-testid="beacon-approved-banner" className="block mb-4 border border-[#14A89A]/50 bg-[#14A89A]/10 px-5 py-4 hover:bg-[#14A89A]/15 transition-colors">
              <div className="flex items-center gap-3">
                <Flame className="text-[#14A89A] shrink-0" size={20} />
                <div className="flex-1">
                  <p className="font-body text-sm text-white font-medium">Your Beacon is approved 🔦</p>
                  <p className="font-body text-xs text-zinc-400 mt-0.5">The Council welcomed you in: tap to set up your creator space on your Profile.</p>
                </div>
                <span className="font-body text-xs text-[#14A89A]">Set it up →</span>
              </div>
            </a>
          )}
          <InviteNudge />
          <VoicesInviteCard />
          <RealmFilter accent="#A855F7" onChange={setSel} />

          {sel.realm === "infrastructure" && <WorkshopBoard domain="infrastructure" />}

          {posts === null ? (
            <div className="flex justify-center py-20"><SignalPulse size={44} /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 border border-white/10">
              <p className="font-heading text-xl text-white mb-2">Silence on this channel</p>
              <p className="font-body text-sm text-zinc-500">Be the first to transmit a signal.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-12">
              {posts.map((p) => <PostCard key={p.id} post={p} onNav={navigate} onDelete={handleDelete} currentUser={user} onTag={setQuery} onChanged={load} />)}
              {hasMore && (
                <button onClick={loadMore} disabled={loadingMore} data-testid="load-more-button" className="mx-auto border border-white/15 text-zinc-300 px-6 py-3 font-body text-sm hover:bg-white/5 transition-colors flex items-center gap-2 disabled:opacity-40">
                  {loadingMore ? <SignalPulse size={16} inline /> : "Load more signals"}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
